import { unwrapResponse, wrapRequest } from "./crypto";
import { apiHeaders, apiHeadersJson } from "./headers";
import { clearToken } from "./token";
import { useCallback, useEffect, useRef, useState } from "react";
import type {
  ApplicationUsage,
  DashboardSummary,
  DocumentDetail,
  DocumentListResponse,
} from "./types";

const BASE = "/api/orchestrator";

class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Called when the API reports the caller is not (or no longer) authenticated.
 *
 * A module-level hook rather than a React context so the plain `get()` helper
 * can report it without every call site having to thread a handler through.
 * The app installs one; a session that expires mid-browse then returns the
 * reader to the sign-in screen instead of showing an error on every panel.
 */
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

/**
 * Exported as `apiGet` for the few reads that happen on a click rather than on
 * mount — revealing a stored secret, which must not run through `useApi`: that
 * re-fetches on remount, and a secret would then reappear on screen because a
 * parent re-rendered.
 */
export { get as apiGet };

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(path, {
    signal,
    headers: apiHeaders(),
    // Stated rather than relied upon. `same-origin` is already fetch's
    // default, so this changes nothing today -- the point is that the session
    // cookie still carries iframe and download requests, which cannot send the
    // bearer header, and if this UI is ever served from a host other than the
    // gateway the default would silently stop sending it. The symptom would be
    // the document viewer 401ing while every panel works, so the dependency is
    // written down where it can be found.
    credentials: "same-origin",
  });

  // 401 is "sign in"; 503 AUTH_NOT_CONFIGURED is "the server cannot let anyone
  // in". Both belong on the sign-in screen rather than in a panel-level error.
  if (res.status === 401 || res.status === 503) {
    // Only on 401, and only there: the token is the thing that was rejected,
    // so keeping it means every later request re-sends a credential the server
    // has already refused. A 503 says the server is misconfigured, which is no
    // reason to throw away a token that may be perfectly good.
    if (res.status === 401) clearToken();
    onUnauthorized?.();
  }

  if (!res.ok) {
    // Surface the server's own message where it sent one -- "no documents for
    // this application" is far more useful to a reviewer than "HTTP 404".
    //
    // Not unwrapped: the server leaves error bodies plain even when encryption
    // is on, precisely so a client that cannot decrypt can still read why.
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.message) detail = body.message;
    } catch {
      /* non-JSON error body; the status line is all we have */
    }
    throw new ApiError(detail, res.status);
  }
  // unwrapResponse is a no-op unless this build encrypts, and even then it
  // decides per response -- some endpoints stay plain by design.
  return unwrapResponse<T>(await res.json());
}

/**
 * A request that changes something.
 *
 * Separate from `get` rather than a flag on it, because the two differ in more
 * than the verb: this one encrypts its body when the build does, and it must
 * not be wired into `useApi`, which retries on remount and would re-issue a
 * client secret or re-create an account as a side effect of a re-render.
 *
 * 403 is deliberately NOT treated as "sign in again". A member calling an
 * admin-only route is correctly signed in and bouncing them to the login screen
 * would loop: sign in, get 403, get bounced, sign in. The caller shows the
 * server's message instead.
 */
export async function mutate<T>(
  path: string,
  // PUT is here for the one endpoint that genuinely replaces a resource rather
  // than amending it: an agent's scored-field set, which is saved whole so a
  // reader's four toggles produce one version instead of four.
  method: "POST" | "PUT" | "PATCH" | "DELETE",
  body?: unknown,
  /**
   * Per-request headers, for the ones that carry meaning rather than transport.
   *
   * X-Tenant-ID is the reason this exists. Without it a caller could build the
   * header and believe it was sent -- which is exactly what happened here: the
   * submit console's tenant field reached the "copy as curl" output and never
   * the request, so every submission went to tenant_default however the field
   * was filled in.
   */
  extraHeaders?: Record<string, string>,
): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: {
      ...(body === undefined ? apiHeaders() : apiHeadersJson()),
      ...(extraHeaders ?? {}),
    },
    credentials: "same-origin",
    body: body === undefined ? undefined : await wrapRequest(body),
  });

  if (res.status === 401) {
    clearToken();
    onUnauthorized?.();
  }

  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const b = await res.json();
      if (b?.message) detail = b.message;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(detail, res.status);
  }

  return unwrapResponse<T>(await res.json());
}

/**
 * A multipart upload.
 *
 * A separate function rather than a branch inside `mutate`, because it has to
 * undo two things that one does unconditionally:
 *
 *   - **No Content-Type.** It must be left unset so the browser writes
 *     `multipart/form-data; boundary=…` itself. Setting it by hand omits the
 *     boundary and the server receives a body it cannot parse -- which surfaces
 *     as a 400 about a missing field, pointing nowhere near the cause.
 *   - **No wrapRequest.** Encrypting the body would turn the file into a JSON
 *     string field, and the ingestion route reads `request.files`.
 *
 * The response is still unwrapped: the server encrypts replies when this build
 * does, regardless of what the request looked like.
 */
export async function mutateForm<T>(
  path: string,
  form: FormData,
  extraHeaders?: Record<string, string>,
): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    headers: { ...apiHeaders(), ...(extraHeaders ?? {}) },
    credentials: "same-origin",
    body: form,
  });

  if (res.status === 401) {
    clearToken();
    onUnauthorized?.();
  }

  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const b = await res.json();
      if (b?.message) detail = b.message;
    } catch {
      /* 413 from nginx is HTML, not JSON — the status line is all there is */
    }
    throw new ApiError(detail, res.status);
  }

  return unwrapResponse<T>(await res.json());
}

export { ApiError };

export interface AsyncState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

/**
 * Small fetch-on-mount hook.
 *
 * Deliberately not a data-fetching library: this app has four screens and no
 * cross-screen cache to invalidate, so a dependency that owns the network layer
 * would be more machinery than the problem needs. The two things that actually
 * matter here are both handled -- an in-flight request is aborted when the key
 * changes (so a fast click through the list can't render a stale row), and a
 * reload is available for the "retry" affordance on the error state.
 */
export function useApi<T>(path: string | null): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [nonce, setNonce] = useState(0);
  const controller = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!path) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    controller.current?.abort();
    const ac = new AbortController();
    controller.current = ac;

    setLoading(true);
    setError(null);

    get<T>(path, ac.signal)
      .then((d) => {
        if (!ac.signal.aborted) {
          setData(d);
          setLoading(false);
        }
      })
      .catch((e: unknown) => {
        // An abort is the expected outcome of navigating away, not a failure to
        // report -- showing it would flash an error on every fast click.
        if (ac.signal.aborted) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });

    return () => ac.abort();
  }, [path, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, loading, reload };
}

export interface ListParams {
  page: number;
  page_size: number;
  document_type?: string;
  status?: string;
  q?: string;
  application_id?: string;
  format_id?: string;
  period?: string;
  date_from?: string;
  date_to?: string;
  sort?: string;
  order?: string;
  /** "0-50" | "50-70" | "70-85" | "85-95" | "95-100" | "unscored" */
  confidence_band?: string;
  /** A console_users user_id, or "none" for documents with no recorded submitter. */
  submitted_by?: string;
}

export function documentListPath(p: ListParams): string {
  const qs = new URLSearchParams();
  Object.entries(p).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  });
  return `${BASE}/documents?${qs.toString()}`;
}

export function applicationListPath(p: {
  page: number;
  page_size: number;
  q?: string;
  period?: string;
  date_from?: string;
  date_to?: string;
  submitted_by?: string;
}): string {
  const qs = new URLSearchParams();
  Object.entries(p).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  });
  return `${BASE}/applications?${qs.toString()}`;
}

export function dashboardPath(
  period: string,
  from?: string,
  to?: string,
  submittedBy?: string,
): string {
  const qs = new URLSearchParams({ period });
  if (from) qs.set("date_from", from);
  if (to) qs.set("date_to", to);
  if (submittedBy) qs.set("submitted_by", submittedBy);
  return `${BASE}/dashboard/summary?${qs.toString()}`;
}

export const platformHealthPath = () => `${BASE}/platform/health`;
export const platformServicePath = (key: string) => `${BASE}/platform/${key}`;

/** `?submitted_by=` appended when set, for the paths that take nothing else. */
const bySubmitter = (path: string, submittedBy?: string) =>
  submittedBy
    ? `${path}${path.includes("?") ? "&" : "?"}submitted_by=${encodeURIComponent(submittedBy)}`
    : path;

export const agentListPath = (submittedBy?: string) =>
  bySubmitter(`${BASE}/agents`, submittedBy);

export const qualitySummaryPath = (submittedBy?: string) =>
  bySubmitter(`${BASE}/quality/summary`, submittedBy);

export const agentDetailPath = (type: string, days = 30, submittedBy?: string) =>
  bySubmitter(`${BASE}/agents/${encodeURIComponent(type)}?days=${days}`, submittedBy);

export function agentLogsPath(
  type: string,
  p: { page: number; page_size: number; types?: string; q?: string },
): string {
  const qs = new URLSearchParams();
  Object.entries(p).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  });
  return `${BASE}/agents/${encodeURIComponent(type)}/logs?${qs.toString()}`;
}

/**
 * Which fields an agent is scored on, plus every field one could be.
 *
 * Its own path rather than a slice of the agent detail response: the toggle
 * card reloads after a save, and reloading the whole agent page to reflect one
 * switch would refetch the histogram, the token series and the router stats
 * with it.
 */
export const agentRequiredFieldsPath = (type: string) =>
  `${BASE}/agents/${encodeURIComponent(type)}/required-fields`;

/**
 * The verification form for one document, and where judgements are saved.
 *
 * Takes a job id or a document id, like the detail route, because the console's
 * URL carries whichever the caller had.
 */
export const verifyPath = (id: string) =>
  `${BASE}/documents/${encodeURIComponent(id)}/golden`;

/** Every extraction attempt on one document, newest first. */
export const documentHistoryPath = (id: string) =>
  `${BASE}/documents/${encodeURIComponent(id)}/history`;

/** Queue a document for extraction again. Admin only — a re-run spends tokens. */
export const reprocessPath = (id: string) =>
  `${BASE}/documents/${encodeURIComponent(id)}/reprocess`;

/** Every document of one application, with how many times each was run. */
export const applicationHistoryPath = (id: string) =>
  `${BASE}/applications/${encodeURIComponent(id)}/history`;

/** The document templates one agent has seen, worst-scoring first. */
export const agentFormatsPath = (type: string) =>
  `${BASE}/agents/${encodeURIComponent(type)}/formats`;

/** Measured accuracy against what people verified — the one non-self-reported number. */
export const accuracyPath = (documentType?: string) =>
  `${BASE}/golden/accuracy${documentType ? `?document_type=${encodeURIComponent(documentType)}` : ""}`;

/** Every value one agent has extracted for one field, newest first. */
export function agentFieldValuesPath(
  type: string,
  field: string,
  p: {
    page: number;
    page_size: number;
    sort?: string;
    fill?: string;
    q?: string;
  },
): string {
  const qs = new URLSearchParams();
  Object.entries(p).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  });
  return (
    `${BASE}/agents/${encodeURIComponent(type)}` +
    `/fields/${encodeURIComponent(field)}?${qs.toString()}`
  );
}

export const documentDetailPath = (id: string) => `${BASE}/documents/${id}`;

/** The original uploaded file, for checking an extraction against its source. */
export const documentFilePath = (id: string) =>
  `${BASE}/documents/${encodeURIComponent(id)}/file`;
export const applicationUsagePath = (id: string) =>
  `${BASE}/applications/${id}/usage`;

export type {
  DocumentListResponse,
  DocumentDetail,
  DashboardSummary,
  ApplicationUsage,
};
